import React, { useState } from 'react';
import { Link2, Check, ExternalLink, Share2, Instagram } from 'lucide-react';
import StoryCardGenerator from './StoryCardGenerator';

interface ShareProfileButtonProps {
  professionalId: number;
  slug?: string | null;
  className?: string;
  professionalData?: {
    nombre: string;
    apellido: string;
    especialidad?: string;
    professional_type?: string;
    business_name?: string;
    picture?: string;
  };
}

const ShareProfileButton: React.FC<ShareProfileButtonProps> = ({
  professionalId,
  slug,
  className = '',
  professionalData
}) => {
  const [copied, setCopied] = useState(false);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const shareUrl = slug
    ? `${API_URL}/p/${slug}`
    : `${API_URL}/p/${professionalId}`;

  const profileUrl = slug
    ? `${window.location.origin}/p/${slug}`
    : `${window.location.origin}/p/${professionalId}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleOpenProfile = () => {
    window.open(profileUrl, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mi perfil en consultAR',
          text: 'Reservá tu turno desde este enlace:',
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled share
      }
    }
  };

  return (
    <>
      {/* text-left: la app centra todo por defecto (.App de CRA) */}
      <div className={`bg-white/60 backdrop-blur-sm rounded-2xl p-5 border border-gray-100 shadow-sm text-left ${className}`}>
        {/* Header compacto */}
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center">
            <Link2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 tracking-tight">Tu link de reservas</h3>
            <p className="text-[11px] text-gray-500">Compartilo en redes, WhatsApp o tu bio</p>
          </div>

          {/* Botón Stories - destacado */}
          {professionalData && (
            <button
              onClick={() => setShowStoryGenerator(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white text-xs font-semibold hover:shadow-md hover:shadow-pink-500/25 active:scale-95 transition-all"
            >
              <Instagram className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Stories</span>
            </button>
          )}
        </div>

        {/* Botones en una fila. Sin el link escrito (oct-2026, Esteban): no
            se usaba para nada y ocupaba la mitad de la fila; "Copiar link" se
            queda con ese lugar. */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyLink}
            className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
              copied
                ? 'bg-green-500 text-white shadow-md shadow-green-500/25'
                : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'
            }`}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>Link copiado</span>
              </>
            ) : (
              <>
                <Link2 className="w-3.5 h-3.5" />
                <span>Copiar link</span>
              </>
            )}
          </button>

          <button
            onClick={handleOpenProfile}
            className="flex-shrink-0 p-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95 transition-all"
            title="Ver perfil"
          >
            <ExternalLink className="w-4 h-4" />
          </button>

          {'share' in navigator && (
            <button
              onClick={handleNativeShare}
              className="flex-shrink-0 p-2.5 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 active:scale-95 transition-all"
              title="Compartir"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Story Card Generator Modal */}
      {professionalData && (
        <StoryCardGenerator
          isOpen={showStoryGenerator}
          onClose={() => setShowStoryGenerator(false)}
          professional={{
            id: professionalId,
            nombre: professionalData.nombre,
            apellido: professionalData.apellido,
            especialidad: professionalData.especialidad,
            professional_type: professionalData.professional_type,
            business_name: professionalData.business_name,
            picture: professionalData.picture,
            slug: slug || undefined,
          }}
        />
      )}
    </>
  );
};

export default ShareProfileButton;
